import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Leaf, ArrowLeft, ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const BUSINESS_TYPES = ['Farm', 'Produce grower', 'Meat producer', 'Poultry producer', 'Egg producer', 'Dairy producer', 'Cheese producer', 'Bakery', 'Maple producer', 'Honey producer', 'Preserves/pantry producer', 'Specialty food producer', 'Artisan/local producer', 'Other'];
const PRODUCT_CATEGORIES = ['Vegetables', 'Fruit', 'Eggs', 'Beef', 'Pork', 'Chicken', 'Lamb', 'Dairy', 'Milk', 'Cheese', 'Bread', 'Baked goods', 'Maple syrup', 'Honey', 'Jams/preserves', 'Pantry items', 'Other local products'];
const COMPLIANCE = ['USDA-inspected products', 'Licensed dairy producer', 'Certified organic', 'Cottage food producer', 'Commercial kitchen', 'Other license/certification', 'None / Not applicable'];
const STEPS = ['Business & Contact', 'Farm & Products', 'Partnership'];

const CheckGroup = ({ options, selected, onToggle, testid }) => (
  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid={testid}>
    {options.map((opt) => (
      <label key={opt} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selected.includes(opt) ? 'border-forest bg-forest/5' : 'border-forest/15 bg-white hover:bg-cream'}`}>
        <Checkbox checked={selected.includes(opt)} onCheckedChange={() => onToggle(opt)} />
        <span className="font-manrope text-sm text-dark">{opt}</span>
      </label>
    ))}
  </div>
);

export default function FarmApply() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const [f, setF] = useState({
    farm_name: '', contact_name: '', email: '', password: '', phone: '',
    address: '', town: '', state: 'NH', zip: '', website_social: '',
    business_types: [], products_available: [], availability: '', preferred_contact: '',
    pricing_option: '', compliance: [], agreed_terms: false,
  });

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const toggle = (k, opt) => setF((prev) => ({ ...prev, [k]: prev[k].includes(opt) ? prev[k].filter((x) => x !== opt) : [...prev[k], opt] }));

  const validateStep = () => {
    if (step === 0) {
      if (!f.farm_name || !f.contact_name || !f.email || !f.password || !f.phone || !f.address || !f.town || !f.state || !f.zip) {
        toast.error('Please complete all required fields');
        return false;
      }
    } else if (step === 1) {
      if (f.business_types.length === 0 || f.products_available.length === 0 || !f.availability || !f.preferred_contact) {
        toast.error('Please complete all required fields');
        return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.pricing_option) { toast.error('Please choose a pricing option'); return; }
    if (!f.agreed_terms) { toast.error('Please agree to the application consent'); return; }
    setLoading(true);
    try {
      const [contact_first_name, ...rest] = f.contact_name.trim().split(' ');
      const contact_last_name = rest.join(' ') || contact_first_name;
      const { email, password, farm_name, phone, agreed_terms, contact_name, ...application } = f;
      await api.post('/farm/apply', { email, password, farm_name, contact_first_name, contact_last_name, phone, agreed_terms, application });
      await checkAuth();
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit application');
    } finally {
      setLoading(false);
    }
  };

  const inp = 'bg-cream border-forest/20';

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen bg-cream flex items-center justify-center" data-testid="farm-apply-confirmation">
        <div className="max-w-lg mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-6"><CheckCircle2 className="w-9 h-9 text-forest" /></div>
          <h1 className="font-playfair text-3xl font-bold text-dark mb-4">Application Submitted!</h1>
          <p className="font-manrope text-dark/70 leading-relaxed mb-8">
            Thank you for applying to become a FarmBox Partner! Destini will review your application and contact you to learn more about your farm and discuss whether FarmBox is a good fit. There is no obligation to participate by submitting an application.
          </p>
          <button onClick={() => navigate('/farm/dashboard')} className="btn-primary" data-testid="apply-view-status">View My Application Status</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="farm-apply-page">
      <div className="container-custom py-12 max-w-3xl">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3"><Leaf className="w-5 h-5 text-forest" /><span className="badge-local">Farm Partner Application</span></div>
          <h1 className="font-playfair text-4xl font-bold text-dark">Apply to Become a FarmBox Partner</h1>
          <p className="font-manrope text-dark/60 mt-3 flex items-center gap-2"><Clock className="w-4 h-4" />Takes about 3–5 minutes. Submitting an application doesn't automatically make you a supplier — FarmBox reviews and approves each farm.</p>
          <p className="font-manrope text-sm text-dark/60 mt-2">Already applied? <Link to="/farm/login" className="text-forest font-medium hover:underline">Farm Partner Login</Link></p>
        </div>

        {/* Progress indicator */}
        <div className="mb-8" data-testid="apply-progress">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, i) => (
              <span key={s} className={`font-manrope text-xs sm:text-sm font-semibold ${i <= step ? 'text-forest' : 'text-dark/30'}`}>{s}</span>
            ))}
          </div>
          <div className="h-2 bg-white rounded-full overflow-hidden border border-forest/10">
            <div className="h-full bg-forest transition-all duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} data-testid="apply-progress-bar" />
          </div>
          <p className="font-manrope text-xs text-dark/40 mt-2">Step {step + 1} of {STEPS.length}</p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          {step === 0 && (
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 shadow-card" data-testid="apply-step-1">
              <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Business &amp; Contact Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Farm / business name *</Label><Input className={inp} data-testid="apply-farm-name" value={f.farm_name} onChange={(e) => set('farm_name', e.target.value)} /></div>
                <div><Label>Primary contact name *</Label><Input className={inp} data-testid="apply-contact-name" value={f.contact_name} onChange={(e) => set('contact_name', e.target.value)} /></div>
                <div><Label>Email *</Label><Input type="email" className={inp} data-testid="apply-email" value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
                <div><Label>Password *</Label><Input type="password" className={inp} data-testid="apply-password" value={f.password} onChange={(e) => set('password', e.target.value)} /></div>
                <div><Label>Phone *</Label><Input className={inp} data-testid="apply-phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
                <div><Label>Business/farm address *</Label><Input className={inp} data-testid="apply-address" value={f.address} onChange={(e) => set('address', e.target.value)} /></div>
                <div><Label>City/Town *</Label><Input className={inp} data-testid="apply-town" value={f.town} onChange={(e) => set('town', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>State *</Label><Input className={inp} data-testid="apply-state" value={f.state} onChange={(e) => set('state', e.target.value)} /></div>
                  <div><Label>ZIP code *</Label><Input className={inp} data-testid="apply-zip" value={f.zip} onChange={(e) => set('zip', e.target.value)} /></div>
                </div>
                <div><Label>Website or social media link (optional)</Label><Input className={inp} data-testid="apply-website-social" value={f.website_social} onChange={(e) => set('website_social', e.target.value)} /></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 shadow-card" data-testid="apply-step-2">
              <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Farm &amp; Product Information</h2>
              <div className="mb-6">
                <Label className="mb-2 block">Producer / business type *</Label>
                <CheckGroup testid="apply-business-types" options={BUSINESS_TYPES} selected={f.business_types} onToggle={(o) => toggle('business_types', o)} />
              </div>
              <div className="mb-6">
                <Label className="mb-2 block">Broad product categories offered *</Label>
                <p className="font-manrope text-sm text-dark/60 mb-3">Individual product listings and pricing come later, after approval.</p>
                <CheckGroup testid="apply-products" options={PRODUCT_CATEGORIES} selected={f.products_available} onToggle={(o) => toggle('products_available', o)} />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="mb-2 block">Availability *</Label>
                  <div className="flex flex-wrap gap-2">
                    {['Year-round', 'Seasonal', 'Both'].map((opt) => (
                      <label key={opt} className={`flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer text-sm font-manrope ${f.availability === opt ? 'border-forest bg-forest text-cream' : 'border-forest/20 bg-white text-dark'}`} data-testid={`apply-availability-${opt.toLowerCase().replace(/[^a-z]/g, '')}`}>
                        <input type="radio" className="hidden" checked={f.availability === opt} onChange={() => set('availability', opt)} />{opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Preferred contact method *</Label>
                  <div className="flex flex-wrap gap-2">
                    {['Email', 'Phone', 'Text'].map((opt) => (
                      <label key={opt} className={`flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer text-sm font-manrope ${f.preferred_contact === opt ? 'border-forest bg-forest text-cream' : 'border-forest/20 bg-white text-dark'}`} data-testid={`apply-contact-method-${opt.toLowerCase()}`}>
                        <input type="radio" className="hidden" checked={f.preferred_contact === opt} onChange={() => set('preferred_contact', opt)} />{opt}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <>
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 shadow-card" data-testid="apply-step-3">
                <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Partnership &amp; Submission</h2>
                <div className="mb-6">
                  <Label className="mb-2 block">Willingness to discuss FarmBox partner/wholesale pricing *</Label>
                  <div className="flex flex-wrap gap-2">
                    {['I already offer wholesale pricing', 'I would like to discuss pricing'].map((opt) => (
                      <label key={opt} className={`flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer text-sm font-manrope ${f.pricing_option === opt ? 'border-forest bg-forest text-cream' : 'border-forest/20 bg-white text-dark'}`} data-testid={`apply-pricing-${opt === 'I already offer wholesale pricing' ? 'have' : 'discuss'}`}>
                        <input type="radio" className="hidden" checked={f.pricing_option === opt} onChange={() => set('pricing_option', opt)} />{opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Basic certifications / licenses</Label>
                  <CheckGroup testid="apply-compliance" options={COMPLIANCE} selected={f.compliance} onToggle={(o) => toggle('compliance', o)} />
                  <p className="font-manrope text-xs text-dark/50 mt-3">Supporting documentation may be requested during partner onboarding.</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 shadow-card">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="apply-terms">
                  <Checkbox checked={f.agreed_terms} onCheckedChange={(v) => set('agreed_terms', v)} className="mt-1" />
                  <span className="font-manrope text-sm text-dark">I consent to submitting this application to FarmBox and understand it does not guarantee partnership.</span>
                </label>
              </div>
            </>
          )}

          <div className="flex items-center justify-between gap-4">
            <button type="button" onClick={back} disabled={step === 0} className="btn-secondary py-2.5 px-5 text-sm inline-flex items-center gap-2 disabled:opacity-0" data-testid="apply-back-btn">
              <ArrowLeft className="w-4 h-4" />Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={next} className="btn-primary py-2.5 px-6 text-sm inline-flex items-center gap-2" data-testid="apply-next-btn">
                Next<ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="submit" disabled={loading} className="btn-primary py-2.5 px-6 text-sm" data-testid="apply-submit">
                {loading ? 'Submitting…' : 'Submit Application'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
